import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

import { CloseMenu } from '@webapp/store/ui/action-creators';

// Components
import Menu from '@components/menu';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';

import { PaymentType } from './billing-input';

// Icons
import CloseIcon from '@components/basics/icons/close';
import PayPalIcon from '@components/basics/icons/pay-pal';
import CreditCardIcon from '@components/basics/icons/credit-card';
import DotsCircleIcon from '@components/basics/icons/dots-circle';
import TracWalletIcon from '@components/basics/icons/trac-wallet';
import VisaLogo from '@components/basics/icons/credit-card-brands/visa';
import AmexLogo from '@components/basics/icons/credit-card-brands/amex';
import MastercardLogo from '@components/basics/icons/credit-card-brands/mastercard';

type CreditCardBrand = 'visa' | 'amex' | 'mastercard';

interface PaymentInputProps {
  className?: string;
  value: React.ReactNode;
  paymentType: PaymentType;
  brand?: CreditCardBrand;
  hasCharges?: string;
  onClick: () => void;
  closeMenu: () => CloseMenu;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

const PaymentInput: FunctionComponent<PaymentInputProps> = ({
  className,
  brand,
  value,
  paymentType,
  hasCharges,
  onClick,
  closeMenu,
}) => {
  const getCreditCardDecoration = (brand: CreditCardBrand) => {
    switch (brand) {
      case 'visa':
        return <VisaLogo className="credit-card-brand" />;
      case 'amex':
        return <AmexLogo className="credit-card-brand" />;
      case 'mastercard':
        return <MastercardLogo className="credit-card-brand" />;

      default:
        break;
    }
  };
  const RightDecorationMenu = (
    <Flex gap={10} alignItems="center" justifyContent="flex-end">
      {brand && paymentType === 'card' && getCreditCardDecoration(brand)}
      <Menu
        className="menu"
        button={<DotsCircleIcon className="open-icon" />}
        closeButton={<CloseIcon onClick={closeMenu} className="close-icon" />}
        customIconHeight="24px"
        menuId={`${paymentType}-menu`}
      >
        <MenuContent>
          <Typography
            weight="700"
            className="menu-item danger"
            onClick={onClick}
          >
            <Translate
              id={
                paymentType === 'card'
                  ? 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.CREDIT_CARD.MENU'
                  : paymentType === 'pay-pal'
                  ? 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.PAYPAL.MENU'
                  : paymentType === 'trac-wallet'
                  ? 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.TRACWALLET.MENU'
                  : ''
              }
            />
          </Typography>
        </MenuContent>
      </Menu>
    </Flex>
  );

  const getLeftDecoration = (paymentType: PaymentType) => {
    switch (paymentType) {
      case 'card':
        return <CreditCardIcon />;
      case 'pay-pal':
        return <PayPalIcon />;
      case 'trac-wallet':
        return <TracWalletIcon />;

      default:
        break;
    }
  };

  const getLabel = (paymentType: PaymentType) => {
    switch (paymentType) {
      case 'card':
        return 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.CREDIT_CARD.LABEL';
      case 'pay-pal':
        return 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.PAYPAL.LABEL';
      case 'trac-wallet':
        return 'ARTIST_PROFILE.SUBSCRIPTION.BILLING.TRACWALLET.LABEL';

      default:
        break;
    }
  };

  const getValue = (paymentType: PaymentType, value: React.ReactNode) => {
    switch (paymentType) {
      case 'card':
        return `**** **** **** ${value}`;

      default:
        return value;
    }
  };

  return (
    <div>
      <InputField
        className={className || ''}
        name="credit-card-input"
        label={<Translate id={getLabel(paymentType)} />}
        value={getValue(paymentType, value)}
        autoComplete="off"
        locked
        leftDecoration={getLeftDecoration(paymentType)}
        rightDecoration={RightDecorationMenu}
      />
      {hasCharges && (
        <ChargesWrapper>
          <Typography type="small" weight="700" className="text">
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.BILLING.HAS_CHARGES.TEXT" />{' '}
            {hasCharges}.
          </Typography>
        </ChargesWrapper>
      )}
    </div>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(styled(PaymentInput)`
  ${(props) =>
    props.hasCharges &&
    css`
      border-radius: 4px 4px 0px 0px;
    `}

  .menu {
    .menu-action {
      z-index: 0;
    }

    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }

  .decoration-right {
    z-index: 2;
    .credit-card-brand {
      max-width: 42px;
    }
  }
`);

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;

const ChargesWrapper = styled.div`
  margin-top: 3px;
  border-radius: 0px 0px 4px 4px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.colors.inputBackground};

  .text {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;
